import React, { useEffect, useState } from "react";
import IncludeSideBar from "../Sidebar/IncludeSideBar";
import { getAllReports } from "../../apis/generate_report";
import useDebounce from "../../utils/useDebounce";
import {
  formatDateTime,
  formatLabel,
  formatRelativeTime,
  formatShortDate,
  getReportTypeDetail,
  resolveMediaUrl,
} from "./reportTypes";
import "./ReportTypeScreen.css";

const DEFAULT_PAGINATION = {
  totalItems: 0,
  totalPages: 1,
  currentPage: 1,
  itemsPerPage: 10,
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 80];

const MEDIA_FILTER_OPTIONS = [
  { value: "all", label: "All Evidence" },
  { value: "with_media", label: "With Media" },
  { value: "image_only", label: "Image Evidence" },
  { value: "video_only", label: "Video Evidence" },
  { value: "no_media", label: "No Media" },
];

const SORT_OPTIONS = [
  { value: "latest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "reporter", label: "Reporter A-Z" },
  { value: "reason", label: "Reason A-Z" },
];

const getVisiblePages = (currentPage, totalPages) => {
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  startPage = Math.max(1, endPage - maxVisiblePages + 1);

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, index) => startPage + index
  );
};

const getReporterName = (report) =>
  report?.reporter?.nickname || report?.reporter?.username || "Unknown Reporter";

const getReporterInitial = (report) => {
  const reporterName = getReporterName(report);
  return reporterName.charAt(0).toUpperCase() || "R";
};

const getReportMediaState = (report) => {
  const imageUrl = resolveMediaUrl(report?.media_image);
  const videoUrl = resolveMediaUrl(report?.media_video);

  return {
    imageUrl,
    videoUrl,
    hasImage: Boolean(imageUrl),
    hasVideo: Boolean(videoUrl),
  };
};

const matchesMediaFilter = (report, mediaFilter) => {
  const { hasImage, hasVideo } = getReportMediaState(report);

  if (mediaFilter === "with_media") {
    return hasImage || hasVideo;
  }

  if (mediaFilter === "image_only") {
    return hasImage;
  }

  if (mediaFilter === "video_only") {
    return hasVideo;
  }

  if (mediaFilter === "no_media") {
    return !hasImage && !hasVideo;
  }

  return true;
};

const sortReports = (reports, sortBy) => {
  const reportList = [...reports];

  if (sortBy === "oldest") {
    return reportList.sort(
      (firstReport, secondReport) =>
        new Date(firstReport?.createdAt || 0) - new Date(secondReport?.createdAt || 0)
    );
  }

  if (sortBy === "reporter") {
    return reportList.sort((firstReport, secondReport) =>
      getReporterName(firstReport).localeCompare(getReporterName(secondReport))
    );
  }

  if (sortBy === "reason") {
    return reportList.sort((firstReport, secondReport) =>
      formatLabel(firstReport?.reason).localeCompare(
        formatLabel(secondReport?.reason)
      )
    );
  }

  return reportList.sort(
    (firstReport, secondReport) =>
      new Date(secondReport?.createdAt || 0) - new Date(firstReport?.createdAt || 0)
  );
};

function ReportTypeScreen({ reportType }) {
  const reportConfig = getReportTypeDetail(reportType);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [mediaFilter, setMediaFilter] = useState("all");
  const [sortBy, setSortBy] = useState("latest");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [preview, setPreview] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const debouncedSearchTerm = useDebounce(searchTerm, 450);

  useEffect(() => {
    let isMounted = true;

    const fetchReports = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getAllReports({
          page: currentPage,
          limit,
          reportType,
          searchTerm: debouncedSearchTerm.trim(),
        });

        if (!isMounted) {
          return;
        }

        setReports(response?.data || []);
        setPagination({
          totalItems: response?.pagination?.totalItems || 0,
          totalPages: response?.pagination?.totalPages || 1,
          currentPage: response?.pagination?.currentPage || currentPage,
          itemsPerPage: response?.pagination?.itemsPerPage || limit,
        });
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        setReports([]);
        setPagination({
          ...DEFAULT_PAGINATION,
          currentPage: 1,
          itemsPerPage: limit,
        });
        setError(
          fetchError?.response?.data?.message ||
            `Failed to load ${reportConfig.title.toLowerCase()}.`
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      isMounted = false;
    };
  }, [currentPage, debouncedSearchTerm, limit, reloadKey, reportConfig.title, reportType]);

  const reasonCountMap = {};
  const evidenceStats = {
    image: 0,
    video: 0,
    both: 0,
    none: 0,
  };

  reports.forEach((report) => {
    const normalizedReason = report?.reason || "unspecified";
    const { hasImage, hasVideo } = getReportMediaState(report);

    reasonCountMap[normalizedReason] = (reasonCountMap[normalizedReason] || 0) + 1;

    if (hasImage && hasVideo) {
      evidenceStats.both += 1;
    } else if (hasImage) {
      evidenceStats.image += 1;
    } else if (hasVideo) {
      evidenceStats.video += 1;
    } else {
      evidenceStats.none += 1;
    }
  });

  const reasonBreakdown = Object.keys(reasonCountMap)
    .map((reasonKey) => ({
      value: reasonKey,
      label: formatLabel(reasonKey),
      count: reasonCountMap[reasonKey],
    }))
    .sort(
      (firstReason, secondReason) =>
        secondReason.count - firstReason.count ||
        firstReason.label.localeCompare(secondReason.label)
    );

  const filteredReports = reports.filter((report) => {
    const normalizedReason = report?.reason || "unspecified";

    if (reasonFilter && normalizedReason !== reasonFilter) {
      return false;
    }

    return matchesMediaFilter(report, mediaFilter);
  });

  const visibleReports = sortReports(filteredReports, sortBy);
  const visibleReasonCountMap = {};

  visibleReports.forEach((report) => {
    const normalizedReason = report?.reason || "unspecified";
    visibleReasonCountMap[normalizedReason] =
      (visibleReasonCountMap[normalizedReason] || 0) + 1;
  });

  const visibleReasonBreakdown = Object.keys(visibleReasonCountMap)
    .map((reasonKey) => ({
      value: reasonKey,
      label: formatLabel(reasonKey),
      count: visibleReasonCountMap[reasonKey],
    }))
    .sort(
      (firstReason, secondReason) =>
        secondReason.count - firstReason.count ||
        firstReason.label.localeCompare(secondReason.label)
    );
  const visiblePages = getVisiblePages(currentPage, pagination.totalPages);
  const uniqueReporters = new Set(
    visibleReports
      .map((report) => report?.reporter_id || report?.reporter?.id)
      .filter(Boolean)
  ).size;
  const reportsWithEvidence = visibleReports.filter((report) => {
    const { hasImage, hasVideo } = getReportMediaState(report);
    return hasImage || hasVideo;
  }).length;
  const newestReport = visibleReports.reduce((latestReport, report) => {
    if (!latestReport) {
      return report;
    }

    if (
      new Date(report?.createdAt || 0).getTime() >
      new Date(latestReport?.createdAt || 0).getTime()
    ) {
      return report;
    }

    return latestReport;
  }, null);
  const showingFrom =
    pagination.totalItems === 0
      ? 0
      : (pagination.currentPage - 1) * pagination.itemsPerPage + 1;
  const showingTo =
    pagination.totalItems === 0
      ? 0
      : Math.min(showingFrom + reports.length - 1, pagination.totalItems);

  const statsCards = [
    {
      title: "Total Reports",
      value: pagination.totalItems,
      helper: "Total matching rows for this report type.",
    },
    {
      title: "Unique Reporters",
      value: uniqueReporters,
      helper: "Distinct reporters visible after local filters.",
    },
    {
      title: "With Evidence",
      value: reportsWithEvidence,
      helper: "Rows with image or video evidence attached.",
    },
    {
      title: "Top Reason",
      value: visibleReasonBreakdown[0]?.label || "None",
      helper: visibleReasonBreakdown[0]
        ? `${visibleReasonBreakdown[0].count} visible reports match this reason.`
        : "No reason data in the visible rows.",
    },
    {
      title: "Latest Activity",
      value: newestReport ? formatShortDate(newestReport.createdAt) : "N/A",
      helper: newestReport
        ? formatRelativeTime(newestReport.createdAt)
        : "No reports loaded yet.",
    },
  ];

  const themeStyles = {
    "--report-screen-hero-start": reportConfig.theme.heroStart,
    "--report-screen-hero-end": reportConfig.theme.heroEnd,
    "--report-screen-accent": reportConfig.theme.accent,
    "--report-screen-accent-soft": reportConfig.theme.accentSoft,
    "--report-screen-accent-text": reportConfig.theme.accentText,
    "--report-screen-chip-bg": reportConfig.theme.chipBg,
    "--report-screen-chip-text": reportConfig.theme.chipText,
  };

  const handleClearAllFilters = () => {
    setSearchTerm("");
    setReasonFilter("");
    setMediaFilter("all");
    setSortBy("latest");
    setLimit(10);
    setCurrentPage(1);
    setReloadKey((previousValue) => previousValue + 1);
  };

  const handleClearLocalFilters = () => {
    setReasonFilter("");
    setMediaFilter("all");
    setSortBy("latest");
  };

  const handleToggleDescription = (reportId) => {
    setExpandedRows((previousRows) => ({
      ...previousRows,
      [reportId]: !previousRows[reportId],
    }));
  };

  const openPreview = (type, url, title, poster = "") => {
    setPreview({ type, url, title, poster });
  };

  return (
    <IncludeSideBar>
      <div className="report-screen-page" style={themeStyles}>
        {/* <section className="report-screen-hero">
          <div className="report-screen-hero-copy">
            <span className="report-screen-eyebrow">{reportConfig.eyebrow}</span>
            <h1>{reportConfig.title}</h1>
            <p>{reportConfig.description}</p>
          </div>

          <div className="report-screen-hero-notes">
            <div className="report-screen-hero-note">
              <strong>Backend Report Type</strong>
              <span>`report_type={reportType}`</span>
            </div>

            <div className="report-screen-hero-note">
              <strong>Search Behavior</strong>
              <span>The `search` query parameter filters by reporter username.</span>
            </div>

            <div className="report-screen-hero-note">
              <strong>Admin Section</strong>
              <span>Placed under {reportConfig.section} for quicker moderation flow.</span>
            </div>
          </div>
        </section> */}

        <section className="report-screen-stats">
          {statsCards.map((card) => (
            <article className="report-screen-stat-card" key={card.title}>
              <span>{card.title}</span>
              <strong>{card.value}</strong>
              <small>{card.helper}</small>
            </article>
          ))}
        </section>

        <section className="report-screen-filters-card">
          <div className="report-screen-filters-grid">
            <label className="report-screen-field">
              <span>Reporter Username</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search reporter username"
              />
            </label>

            <label className="report-screen-field">
              <span>Reason Filter</span>
              <select
                value={reasonFilter}
                onChange={(event) => setReasonFilter(event.target.value)}
              >
                <option value="">All Reasons</option>
                {reasonBreakdown.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label} ({reason.count})
                  </option>
                ))}
              </select>
            </label>

            <label className="report-screen-field">
              <span>Evidence Filter</span>
              <select
                value={mediaFilter}
                onChange={(event) => setMediaFilter(event.target.value)}
              >
                {MEDIA_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="report-screen-field">
              <span>Sort By</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="report-screen-field report-screen-field-compact">
              <span>Rows Per Page</span>
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setCurrentPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </label>

            <div className="report-screen-actions">
              <button
                className="report-screen-primary-btn"
                type="button"
                onClick={() => setReloadKey((previousValue) => previousValue + 1)}
              >
                Refresh
              </button>

              <button
                className="report-screen-secondary-btn"
                type="button"
                onClick={handleClearAllFilters}
              >
                Clear All
              </button>
            </div>
          </div>

          <p className="report-screen-filter-helper">{reportConfig.helper}</p>
        </section>

        {error ? <div className="report-screen-error">{error}</div> : null}

        {/* <section className="report-screen-insights-card">
          <div className="report-screen-section-header">
            <div>
              <h2>Current Page Insights</h2>
              <p>Quick filters and evidence breakdown for the rows currently loaded from the API.</p>
            </div>

            <button
              className="report-screen-ghost-btn"
              type="button"
              onClick={handleClearLocalFilters}
            >
              Clear Local Filters
            </button>
          </div>

          <div className="report-screen-insights-grid">
            <article className="report-screen-insight-panel">
              <span className="report-screen-panel-label">Reason Breakdown</span>
              <div className="report-screen-chip-list">
                <button
                  type="button"
                  className={`report-screen-chip ${reasonFilter === "" ? "active" : ""}`}
                  onClick={() => setReasonFilter("")}
                >
                  <span>All Reasons</span>
                  <strong>{reports.length}</strong>
                </button>

                {reasonBreakdown.length > 0 ? (
                  reasonBreakdown.map((reason) => (
                    <button
                      type="button"
                      key={reason.value}
                      className={`report-screen-chip ${
                        reasonFilter === reason.value ? "active" : ""
                      }`}
                      onClick={() => setReasonFilter(reason.value)}
                    >
                      <span>{reason.label}</span>
                      <strong>{reason.count}</strong>
                    </button>
                  ))
                ) : (
                  <span className="report-screen-muted-copy">
                    No reason data on this page yet.
                  </span>
                )}
              </div>
            </article>

            <article className="report-screen-insight-panel">
              <span className="report-screen-panel-label">Evidence Snapshot</span>
              <div className="report-screen-metric-grid">
                <div className="report-screen-mini-metric">
                  <strong>{evidenceStats.image}</strong>
                  <span>Image Only</span>
                </div>

                <div className="report-screen-mini-metric">
                  <strong>{evidenceStats.video}</strong>
                  <span>Video Only</span>
                </div>

                <div className="report-screen-mini-metric">
                  <strong>{evidenceStats.both}</strong>
                  <span>Image + Video</span>
                </div>

                <div className="report-screen-mini-metric">
                  <strong>{evidenceStats.none}</strong>
                  <span>No Media</span>
                </div>
              </div>
            </article>
          </div>
        </section> */}

        <section className="report-screen-table-card">
          <div className="report-screen-section-header report-screen-table-header">
            <div>
              <h2>{reportConfig.title}</h2>
            </div>

            <div className="report-screen-context-tags">
              <span className="report-screen-context-tag">
                Page {pagination.currentPage} / {pagination.totalPages}
              </span>
            </div>
          </div>

          {loading ? (
            <div className="report-screen-status">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="report-screen-empty-state">
              <h3>No reports found</h3>
              <p>Try a different reporter username or refresh the report queue.</p>
            </div>
          ) : visibleReports.length === 0 ? (
            <div className="report-screen-empty-state">
              <h3>No rows match the current page filters</h3>
              <p>Clear the local reason or evidence filters to see the loaded results again.</p>
              <button
                className="report-screen-primary-btn"
                type="button"
                onClick={handleClearLocalFilters}
              >
                Clear Local Filters
              </button>
            </div>
          ) : (
            <div className="report-screen-table-shell">
              <table className="report-screen-table">
                <thead>
                  <tr>
                    <th>Report</th>
                    <th>Reporter</th>
                    <th>{reportConfig.targetLabel}</th>
                    <th>Reason</th>
                    <th>Description</th>
                    <th>Evidence</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleReports.map((report) => {
                    const reporterName = getReporterName(report);
                    const description =
                      report?.description || "Reporter did not add extra context.";
                    const isExpanded = Boolean(expandedRows[report?.id]);
                    const isLongDescription = description.length > 110;
                    const previewDescription =
                      isLongDescription && !isExpanded
                        ? `${description.slice(0, 110)}...`
                        : description;
                    const { imageUrl, videoUrl, hasImage, hasVideo } =
                      getReportMediaState(report);

                    return (
                      <tr key={report?.id}>
                        <td>
                          <div className="report-screen-stack">
                            <strong>{report?.id}</strong>
                            <span>Original #{report?.original_id ?? "N/A"}</span>
                            <small>{formatLabel(report?.report_type)}</small>
                          </div>
                        </td>

                        <td>
                          <div className="report-screen-reporter">
                            {report?.reporter?.profile_pic ? (
                              <img
                                src={report.reporter.profile_pic}
                                alt={reporterName}
                                className="report-screen-avatar"
                              />
                            ) : (
                              <div className="report-screen-avatar-fallback">
                                {getReporterInitial(report)}
                              </div>
                            )}

                            <div className="report-screen-stack">
                              <strong>{reporterName}</strong>
                              <span>@{report?.reporter?.username || "unknown"}</span>
                              <small>Reporter ID #{report?.reporter_id ?? "N/A"}</small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="report-screen-stack">
                            <strong>
                              {reportConfig.targetLabel} #{report?.target_id ?? "N/A"}
                            </strong>
                            <span>Section: {reportConfig.section}</span>
                            <small>Original ID #{report?.original_id ?? "N/A"}</small>
                          </div>
                        </td>

                        <td>
                          <span className="report-screen-pill">
                            {formatLabel(report?.reason || "unspecified")}
                          </span>
                        </td>

                        <td>
                          <p className="report-screen-description">{previewDescription}</p>
                          {isLongDescription ? (
                            <button
                              type="button"
                              className="report-screen-inline-btn"
                              onClick={() => handleToggleDescription(report?.id)}
                            >
                              {isExpanded ? "Show less" : "Show more"}
                            </button>
                          ) : null}
                        </td>

                        <td>
                          <div className="report-screen-media-stack">
                            {hasImage ? (
                              <button
                                type="button"
                                className="report-screen-thumb-button"
                                onClick={() =>
                                  openPreview("image", imageUrl, `${report?.id} image evidence`)
                                }
                              >
                                <img
                                  src={imageUrl}
                                  alt={`${report?.id} evidence`}
                                  className="report-screen-thumb"
                                />
                                <span>View image evidence</span>
                              </button>
                            ) : null}

                            {hasVideo ? (
                              <button
                                type="button"
                                className="report-screen-media-btn"
                                onClick={() =>
                                  openPreview(
                                    "video",
                                    videoUrl,
                                    `${report?.id} video evidence`,
                                    imageUrl
                                  )
                                }
                              >
                                View video evidence
                              </button>
                            ) : null}

                            {!hasImage && !hasVideo ? (
                              <span className="report-screen-no-media">
                                No evidence attached
                              </span>
                            ) : (
                              <div className="report-screen-mini-badges">
                                {hasImage ? (
                                  <span className="report-screen-mini-badge">Image</span>
                                ) : null}
                                {hasVideo ? (
                                  <span className="report-screen-mini-badge">Video</span>
                                ) : null}
                              </div>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="report-screen-stack">
                            <strong>{formatShortDate(report?.createdAt)}</strong>
                            <span>{formatDateTime(report?.createdAt)}</span>
                            <small>{formatRelativeTime(report?.createdAt)}</small>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination.totalPages > 1 ? (
            <div className="report-screen-pagination">
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1 || loading}
              >
                First
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  className={page === currentPage ? "active" : ""}
                  onClick={() => setCurrentPage(page)}
                  disabled={loading}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) => Math.min(page + 1, pagination.totalPages))
                }
                disabled={currentPage === pagination.totalPages || loading}
              >
                Next
              </button>

              <button
                type="button"
                onClick={() => setCurrentPage(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages || loading}
              >
                Last
              </button>
            </div>
          ) : null}
        </section>

        {preview ? (
          <div
            className="report-screen-preview-backdrop"
            onClick={() => setPreview(null)}
          >
            <div
              className="report-screen-preview-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="report-screen-preview-close"
                type="button"
                onClick={() => setPreview(null)}
              >
                Close
              </button>

              <div className="report-screen-preview-header">
                <h3>{preview.title}</h3>
                <a href={preview.url} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              </div>

              {preview.type === "image" ? (
                <img
                  src={preview.url}
                  alt={preview.title}
                  className="report-screen-preview-image"
                />
              ) : (
                <video
                  src={preview.url}
                  poster={preview.poster || undefined}
                  className="report-screen-preview-video"
                  controls
                  autoPlay
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </IncludeSideBar>
  );
}

export default ReportTypeScreen;
